/**
 * RET-2452 (KTD-3, KTD-4, KTD-7): the state of the past-due payment zone and
 * the steps of its payment. Lives apart from `SubscriptionManager.hook.ts`,
 * which only feeds it and puts `pastDuePayment` in the context. The view is
 * built by the pure `buildPastDuePaymentView`; every payment decision is taken
 * by the pure machine in `PastDuePayment.payment.ts`. This file only runs the
 * steps: the POSTs, Stripe's authentication, the timers and the re-reads.
 */
import type { Stripe } from '@stripe/stripe-js';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { LLog, LOG_COMPONENTS, LOG_OPERATIONS } from 'src/lib/logging';
import SubscriptionService from 'src/providers/SubscriptionProvider/Subscription.service';
import type { tSubscriptionSummary } from 'src/providers/SubscriptionProvider/Subscription.types';
import { eAlertType } from 'src/providers/ToastProvider/Toast.enum';
import type { tAlert } from 'src/providers/ToastProvider/Toast.types';
import { formatAmountOwed } from 'src/utils/pastDueDisclosure';
import {
  isExpiredCard,
  PAST_DUE_HOSTED_OPTION,
  type tPastDuePaymentView,
} from './components/SubscriptionOverview/PastDuePaymentZone.types';
import {
  buildPastDuePaymentView,
  isPastDueZoneOn,
  PAST_DUE_LOCAL_INITIAL_STATE,
  readDebtInvoiceIds,
  resolvePastDuePreviousPaymentMethods,
  type tPastDueLocalState,
  type tPastDueSummaryState,
} from './PastDuePayment.functions';
import {
  describePastDueOutcome,
  ourErrorDecline,
  PAST_DUE_AUTHENTICATION_TIMEOUT_MS,
  PAST_DUE_SETTLING_INTERVAL_MS,
  pastDuePartialAmounts,
  PAST_DUE_SETTLING_MAX_READS,
  resolvePastDueAuthentication,
  resolvePastDuePostAnswer,
  startPastDueAttempt,
  type tPastDueAttempt,
  type tPastDueAttemptOutcome,
  type tPastDuePostAnswer,
  type tPastDueStep,
} from './PastDuePayment.payment';
import {
  isSubscriptionPastDue,
  readRedirectReturnSnapshot,
} from './SubscriptionManager.functions';
import { SubscriptionManagerService } from './SubscriptionManager.service';
import type {
  tPaymentMethod,
  tPaymentMethodDefaults,
  tPreviousPaymentMethods,
} from './SubscriptionManager.types';

export type tUsePastDuePaymentOptions = {
  workspaceId: string | undefined;
  /** `isPastDuePaymentZoneEnabled`. Off: no view, no payment. */
  enabled: boolean;
  /** Owner or Admin: the only roles leadbilling lets pay (RO-1). */
  canPay: boolean;
  subscription: tSubscriptionSummary;
  summaryState: tPastDueSummaryState;
  daysPastDue: number | null;
  paymentMethods: tPaymentMethod[];
  /** The card list was answered at least once (`paymentMethodsLoadState === 'loaded'`). */
  paymentMethodsLoaded: boolean;
  paymentMethodDefaults: tPaymentMethodDefaults;
  /** The SetupIntent of a bank-redirect card add this page load came back from, if any. */
  readReturnSetupIntentId: () => string | null;
  stripe: Stripe | null;
  /** The subscription provider's `getSummary`: refreshes what every screen reads. */
  refreshSummary: () => Promise<void>;
  refreshPaymentMethods: () => Promise<void>;
  /** Opens the existing add-card modal. */
  openAddPaymentMethod: () => void;
  notify: (alert: tAlert) => void;
  translate: (key: string, values?: Record<string, string>) => string;
};

export type tUsePastDuePayment = {
  /** `undefined` with the flag off, or when the plan is not past due. */
  pastDuePayment: tPastDuePaymentView | undefined;
  /** The zone is on screen for someone who can pay: flag on, past due, Owner or Admin. */
  isPastDueZoneActive: boolean;
  /** Puts a card in the zone's selection (the card just added, `tarjeta-guardada`). */
  selectPastDueCard: (paymentMethodId: string) => void;
};

const sleep = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });

/** Sentinel resolved by the watchdog when the raced promise outlived the cap. */
type tAuthenticationTimeout = { timedOut: true };

/**
 * Races a promise against a timeout, clearing the timer either way so a
 * resolved promise never leaves a dangling handle (REL-1, as in
 * `UnlockTrialCapModal.hook.ts`).
 */
function raceWithTimeout<T>(
  promise: Promise<T>,
  ms: number,
): Promise<T | tAuthenticationTimeout> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const watchdog = new Promise<tAuthenticationTimeout>((resolve) => {
    timer = setTimeout(() => resolve({ timedOut: true }), ms);
  });
  return Promise.race([promise, watchdog]).finally(() => clearTimeout(timer));
}

function isAuthenticationTimeout(
  value: unknown,
): value is tAuthenticationTimeout {
  return (
    typeof value === 'object' &&
    value !== null &&
    'timedOut' in value &&
    value.timedOut === true
  );
}

/** What settled a bank authentication that outlived the watchdog. */
type tOutlivedAuthentication =
  'authenticated' | 'incomplete' | 'paid' | 'stale';

/**
 * The invoices Stripe is authenticating: what the POST expected, less what
 * leadbilling said remains after the action. Empty when that is unknown.
 */
function authenticatedInvoiceIds(attempt: tPastDueAttempt): string[] {
  const next = attempt.authentication?.next;
  if (!next) return [];
  return attempt.request.expected_invoice_ids.filter(
    (id) => !next.invoiceIds.includes(id),
  );
}

/** Money may have left the customer in this attempt: every screen must re-read. */
function mayHaveCharged(
  attempt: tPastDueAttempt,
  outcome: tPastDueAttemptOutcome | null,
): boolean {
  return attempt.charged > 0 || outcome?.kind === 'paid';
}

function logPastDue(message: string, attemptId: string | null) {
  // Ids and outcomes only: never amounts or card data.
  LLog.info(
    {
      component: LOG_COMPONENTS.SUBSCRIPTION,
      method: 'PastDuePayment.hook',
      operation: LOG_OPERATIONS.UI_INTERACTION,
    },
    attemptId ? `${message} (attempt_id ${attemptId})` : message,
  );
}

export function usePastDuePayment(
  options: tUsePastDuePaymentOptions,
): tUsePastDuePayment {
  const [local, setLocal] = useState<tPastDueLocalState>(
    PAST_DUE_LOCAL_INITIAL_STATE,
  );
  const latest = useRef(options);
  latest.current = options;
  // The view of the latest render, for the callbacks: they act on what the
  // customer sees, never on a closure of an older render.
  const viewRef = useRef<tPastDuePaymentView | undefined>(undefined);
  // KTD-4: taken synchronously, before the first await, so two clicks in the
  // same tick send one POST. Released when the attempt ends. It holds the
  // `attempt_id`, not a boolean: an attempt left behind by a workspace switch
  // must not release the lock of the attempt that started after it.
  const payLockRef = useRef<string | null>(null);
  // KTD-7: the cards as the zone found them, per workspace.
  const previousRef = useRef<{
    workspaceId: string;
    value: tPreviousPaymentMethods;
  } | null>(null);
  // One settling loop at a time; the unmount stops it.
  const settlingRef = useRef<{ cancelled: boolean } | null>(null);
  const settledOnLoadRef = useRef<string | null>(null);
  const hostedAmountRef = useRef<number | null>(null);
  const hostedReadingRef = useRef<boolean>(false);
  const unmountedRef = useRef<boolean>(false);

  const isPastDue = isSubscriptionPastDue(options.subscription.status);
  const isPastDueZoneActive =
    options.summaryState === 'loaded' &&
    isPastDueZoneOn({
      zoneEnabled: options.enabled,
      isPastDue,
      canPay: options.canPay,
    });

  useEffect(() => {
    unmountedRef.current = false;
    return () => {
      unmountedRef.current = true;
      if (settlingRef.current) settlingRef.current.cancelled = true;
    };
  }, []);

  // A workspace switch is a full reload today; this keeps it right if not.
  useEffect(() => {
    setLocal(PAST_DUE_LOCAL_INITIAL_STATE);
    payLockRef.current = null;
    if (settlingRef.current) settlingRef.current.cancelled = true;
  }, [options.workspaceId]);

  // KTD-7: note the cards when the zone opens, before any promotion. After a
  // bank redirect the defaults already moved, so the snapshot RET-1974 kept
  // for THIS return is read instead. Taken again only when the zone closes.
  useEffect(() => {
    const workspaceId = options.workspaceId;
    if (!isPastDueZoneActive || workspaceId === undefined) {
      previousRef.current = null;
      return;
    }
    if (!options.paymentMethodsLoaded) return;
    if (previousRef.current?.workspaceId === workspaceId) return;
    const returnSetupIntentId = latest.current.readReturnSetupIntentId();
    previousRef.current = {
      workspaceId,
      value: resolvePastDuePreviousPaymentMethods({
        defaults: options.paymentMethodDefaults,
        returnSetupIntentId,
        snapshot:
          returnSetupIntentId === null
            ? null
            : readRedirectReturnSnapshot(workspaceId),
      }),
    };
  }, [
    isPastDueZoneActive,
    options.workspaceId,
    options.paymentMethodsLoaded,
    options.paymentMethodDefaults,
  ]);

  /** A fresh read of the summary, for a decision; `null` when it fails. */
  const readSummary = useCallback(
    async (workspaceId: string): Promise<tSubscriptionSummary | null> => {
      try {
        return await SubscriptionService.getSummary(workspaceId);
      } catch {
        return null;
      }
    },
    [],
  );

  const announcePaid = useCallback((amountMinorUnits: number | null) => {
    const { notify, translate, subscription } = latest.current;
    const amount = formatAmountOwed({
      amountMinorUnits,
      currency: subscription.currency,
    });
    if (amount === null) return;
    notify({
      type: eAlertType.SUCCESS,
      title: translate('pastDuePayment.paidTitle'),
      message: translate('pastDuePayment.paid', { amount }),
    });
  }, []);

  /** The plan left past due: refresh what every screen reads, then say it. */
  const finishPaid = useCallback(
    async (amountForToast: number | null) => {
      await latest.current.refreshSummary();
      if (unmountedRef.current) return;
      setLocal(PAST_DUE_LOCAL_INITIAL_STATE);
      announcePaid(amountForToast);
    },
    [announcePaid],
  );

  /**
   * `settling`: the payment is done but the summary is still past due. Read it
   * every 3 s, up to 10 times. At the cap the zone stays in `settling`, which
   * already says the payment went through; no toast.
   */
  const settle = useCallback(
    async (options2: { workspaceId: string; amountForToast: number | null }) => {
      if (settlingRef.current && !settlingRef.current.cancelled) return;
      const token = { cancelled: false };
      settlingRef.current = token;
      try {
        for (let read = 0; read < PAST_DUE_SETTLING_MAX_READS; read += 1) {
          await sleep(PAST_DUE_SETTLING_INTERVAL_MS);
          if (token.cancelled) return;
          const summary = await readSummary(options2.workspaceId);
          if (token.cancelled) return;
          if (summary && !isSubscriptionPastDue(summary.status)) {
            await finishPaid(options2.amountForToast);
            return;
          }
        }
        logPastDue('Past-due settling reached its read cap', null);
      } finally {
        if (settlingRef.current === token) settlingRef.current = null;
      }
    },
    [finishPaid, readSummary],
  );

  /**
   * The bank authentication outlived the watchdog. `confirmPayment` cannot be
   * cancelled, so it may still charge: ending the attempt here would offer a
   * retry with a new `attempt_id` while the first one is alive. The attempt
   * stays in `confirming` with its lock, and the first of these wins:
   * - the original promise settles: success → `authenticated` (the same
   *   attempt goes on); Stripe's error or a throw → `incomplete`;
   * - a summary re-read (every 3 s, up to 10) shows the plan out of past due
   *   → `paid`, or the authenticated invoice gone with debt left →
   *   `authenticated`;
   * - the attempt went stale (unmount, workspace switch) → `stale`.
   * Whatever comes second is a no-op. After the read cap only the promise
   * can decide: the zone stays in `confirming` until a reload.
   */
  const reconcileOutlivedAuthentication = useCallback(
    (options2: {
      confirmation: Promise<{ error?: unknown } | undefined>;
      attempt: tPastDueAttempt;
      workspaceId: string;
      stale: () => boolean;
    }): Promise<tOutlivedAuthentication> =>
      new Promise<tOutlivedAuthentication>((resolve) => {
        let decided = false;
        const decide = (value: tOutlivedAuthentication) => {
          if (decided) return;
          decided = true;
          logPastDue(
            `Past-due authentication reconciled: ${value}`,
            options2.attempt.attemptId,
          );
          resolve(value);
        };
        options2.confirmation.then(
          (result) => decide(result?.error ? 'incomplete' : 'authenticated'),
          () => decide('incomplete'),
        );
        const authenticatedIds = authenticatedInvoiceIds(options2.attempt);
        void (async () => {
          for (let read = 0; read < PAST_DUE_SETTLING_MAX_READS; read += 1) {
            await sleep(PAST_DUE_SETTLING_INTERVAL_MS);
            if (decided) return;
            if (options2.stale()) {
              decide('stale');
              return;
            }
            const summary = await readSummary(options2.workspaceId);
            if (decided) return;
            if (summary === null) continue;
            if (!isSubscriptionPastDue(summary.status)) {
              decide('paid');
              return;
            }
            const remaining = summary.amountRemaining;
            const listed = Array.isArray(summary.debtInvoices)
              ? summary.debtInvoices.map((invoice) => invoice.invoiceId)
              : null;
            if (
              authenticatedIds.length > 0 &&
              listed !== null &&
              typeof remaining === 'number' &&
              remaining > 0 &&
              authenticatedIds.every((id) => !listed.includes(id))
            ) {
              decide('authenticated');
              return;
            }
          }
          if (!decided) {
            logPastDue(
              'Past-due authentication reached its read cap unreconciled',
              options2.attempt.attemptId,
            );
          }
        })();
      }),
    [readSummary],
  );

  const runAttempt = useCallback(
    async (attempt: tPastDueAttempt, workspaceId: string) => {
      const stale = () =>
        unmountedRef.current || latest.current.workspaceId !== workspaceId;
      // The attempt outlived its screen (unmount or workspace switch). Its zone
      // is gone, but money it already took must still reach what every screen
      // reads: the provider's `getSummary` guards its own workspace.
      const leave = (
        left: tPastDueAttempt,
        outcome: tPastDueAttemptOutcome | null,
      ) => {
        logPastDue(
          'Past-due payment left behind by its screen',
          left.attemptId,
        );
        if (!mayHaveCharged(left, outcome)) return;
        const { refreshSummary, refreshPaymentMethods } = latest.current;
        void refreshSummary();
        void refreshPaymentMethods();
      };
      let step: tPastDueStep = { kind: 'post', attempt, delayMs: 0 };
      while (step.kind !== 'finish') {
        if (step.kind === 'post' && step.delayMs > 0) await sleep(step.delayMs);
        // Before every POST and before the bank's authentication: a stale
        // attempt never opens a 3DS dialog over another workspace.
        if (stale()) {
          leave(step.attempt, null);
          return;
        }
        if (step.kind === 'post') {
          let answer: tPastDuePostAnswer;
          try {
            const body = await SubscriptionManagerService.payPendingDebtV2(
              workspaceId,
              step.attempt.request,
            );
            answer = { ok: true, body };
          } catch (rejected: unknown) {
            // `payPendingDebtV2` rejects with the body, not the HTTP status:
            // `undefined` is a network error.
            answer = { ok: false, body: rejected };
          }
          step = resolvePastDuePostAnswer({ attempt: step.attempt, answer });
        } else {
          const { stripe } = latest.current;
          let authenticated = false;
          // No Stripe.js, a throw or Stripe's own error: the invoice stays
          // `requires_action`, so it is `authentication_incomplete` (retry
          // allowed), never our error. The watchdog is NOT one of them: the
          // promise is not cancelled and may still charge, so the attempt
          // keeps its lock until the payment reconciles.
          if (stripe) {
            const confirmation = stripe.confirmPayment({
              clientSecret: step.clientSecret,
              redirect: 'if_required' as const,
            });
            try {
              const first = await raceWithTimeout(
                confirmation,
                PAST_DUE_AUTHENTICATION_TIMEOUT_MS,
              );
              if (isAuthenticationTimeout(first)) {
                const authenticating: tPastDueAttempt = step.attempt;
                logPastDue(
                  'Past-due authentication outlived the watchdog',
                  authenticating.attemptId,
                );
                const reconciled = await reconcileOutlivedAuthentication({
                  confirmation,
                  attempt: authenticating,
                  workspaceId,
                  stale,
                });
                // The invoice under authentication may have been charged.
                const charged =
                  authenticating.charged +
                  (authenticating.authentication?.invoiceAmount ?? 0);
                if (reconciled === 'stale') {
                  leave({ ...authenticating, charged }, null);
                  return;
                }
                if (reconciled === 'paid') {
                  step = {
                    kind: 'finish',
                    attempt: {
                      ...authenticating,
                      charged,
                      authentication: null,
                    },
                    outcome: { kind: 'paid', charged },
                  };
                  continue;
                }
                authenticated = reconciled === 'authenticated';
              } else {
                authenticated = !first?.error;
              }
            } catch {
              authenticated = false;
            }
          }
          step = resolvePastDueAuthentication({
            attempt: step.attempt,
            authenticated,
            now: new Date(),
          });
        }
      }
      const { outcome } = step;
      const ended = step.attempt;
      if (stale()) {
        leave(ended, outcome);
        return;
      }

      logPastDue(`Past-due payment ended: ${outcome.kind}`, ended.attemptId);
      const shown = describePastDueOutcome({ outcome, attempt: ended });
      const { refreshSummary, refreshPaymentMethods } = latest.current;

      switch (shown.followUp) {
        case 'confirmPaid': {
          void refreshPaymentMethods();
          const summary = await readSummary(workspaceId);
          if (stale()) {
            void latest.current.refreshSummary();
            return;
          }
          if (summary && !isSubscriptionPastDue(summary.status)) {
            await finishPaid(ended.amountShown);
            return;
          }
          setLocal((current) => ({
            ...current,
            phase: 'settling',
            decline: null,
            partial: null,
          }));
          void settle({ workspaceId, amountForToast: ended.amountShown });
          return;
        }
        case 'rereadDebt': {
          if (ended.charged > 0) void refreshPaymentMethods();
          const summary = await readSummary(workspaceId);
          if (stale()) {
            leave(ended, outcome);
            return;
          }
          if (summary === null) {
            setLocal((current) => ({
              ...current,
              phase: 'idle',
              decline: ourErrorDecline(),
              partial: null,
            }));
            // Our error: the provider's summary is read once too.
            void refreshSummary();
            return;
          }
          await refreshSummary();
          if (stale()) return;
          const remaining = summary.amountRemaining;
          const stillOwes = typeof remaining === 'number' && remaining > 0;
          // Money left in this attempt before the debt changed: say how much,
          // as a partial charge does, never «No se cobró nada».
          if (stillOwes && ended.charged > 0) {
            setLocal((current) => ({
              ...current,
              phase: 'partial',
              decline: null,
              partial: pastDuePartialAmounts({
                attempt: ended,
                charged: ended.charged,
                remaining,
              }),
            }));
            return;
          }
          setLocal((current) => ({
            ...current,
            // Nothing left to pay: the summary's own phase takes over.
            phase: stillOwes ? 'debtChanged' : 'idle',
            decline: null,
            partial: null,
          }));
          return;
        }
        case 'rereadCards':
          setLocal((current) => ({
            ...current,
            ...shown.local,
            selectedPaymentMethodId: null,
          }));
          await refreshPaymentMethods();
          return;
        case 'rereadSummary':
          setLocal((current) => ({ ...current, ...shown.local }));
          if (ended.charged > 0) void refreshPaymentMethods();
          await refreshSummary();
          return;
      }
    },
    [finishPaid, readSummary, settle, reconcileOutlivedAuthentication],
  );

  const onPay = useCallback(() => {
    if (payLockRef.current !== null) return;
    const view = viewRef.current;
    const current = latest.current;
    const workspaceId = current.workspaceId;
    if (!view || workspaceId === undefined || !current.canPay) return;
    // Our error: the payment may have gone through. Only a reload clears it.
    if (view.decline?.group === 'our_error') return;
    if (view.phase !== 'idle' && view.phase !== 'partial' &&
        view.phase !== 'debtChanged' && view.phase !== 'hostedOpened') {
      return;
    }

    if (view.selectedPaymentMethodId === PAST_DUE_HOSTED_OPTION) {
      if (!view.hostedPaymentUrl) return;
      // Opened from the click's own handler, or the browser blocks it.
      window.open(view.hostedPaymentUrl, '_blank', 'noopener,noreferrer');
      hostedAmountRef.current =
        typeof current.subscription.amountRemaining === 'number'
          ? current.subscription.amountRemaining
          : null;
      setLocal((state) => ({ ...state, phase: 'hostedOpened' }));
      return;
    }

    const card = current.paymentMethods.find(
      (pm) => pm.paymentMethodId === view.selectedPaymentMethodId,
    );
    if (!card || isExpiredCard(card)) return;
    if (
      view.decline &&
      view.decline.paymentMethodId === card.paymentMethodId &&
      view.decline.group !== 'authentication_incomplete' &&
      !view.decline.retryAllowed
    ) {
      return;
    }
    const amountRemaining = current.subscription.amountRemaining;
    if (typeof amountRemaining !== 'number' || amountRemaining <= 0) return;
    if (!current.subscription.currency) return;
    // The ids of that amount, or no POST: without them leadbilling answers
    // `debt_changed` forever (the view already reads `amountUnavailable`).
    const invoiceIds = readDebtInvoiceIds(current.subscription);
    if (invoiceIds === null) return;

    const attemptId = crypto.randomUUID();
    payLockRef.current = attemptId;
    const attempt = startPastDueAttempt({
      attemptId,
      card,
      amountRemaining,
      currency: current.subscription.currency,
      invoiceIds,
      previousPaymentMethods:
        previousRef.current?.workspaceId === workspaceId
          ? previousRef.current.value
          : {
              plan: current.paymentMethodDefaults.planPaymentMethodId ?? null,
              account:
                current.paymentMethodDefaults.customerDefaultPaymentMethodId ??
                null,
            },
    });
    logPastDue('Past-due payment started', attempt.attemptId);
    // The card of the attempt stays selected: a decline is shown under it.
    setLocal({
      phase: 'confirming',
      selectedPaymentMethodId: card.paymentMethodId,
      decline: null,
      partial: null,
    });
    void runAttempt(attempt, workspaceId)
      .catch((error: unknown) => {
        LLog.error(
          {
            component: LOG_COMPONENTS.SUBSCRIPTION,
            method: 'PastDuePayment.hook',
            operation: LOG_OPERATIONS.ERROR,
          },
          'Past-due payment stopped without an outcome',
          error instanceof Error ? error : new Error(String(error)),
        );
        // Our error, and the charge may have gone through: read the summary
        // once (the provider guards its own workspace and unmount).
        void latest.current.refreshSummary();
        if (
          !unmountedRef.current &&
          latest.current.workspaceId === workspaceId
        ) {
          setLocal((state) => ({
            ...state,
            phase: 'idle',
            decline: ourErrorDecline(),
            partial: null,
          }));
        }
      })
      .finally(() => {
        // Only the attempt that holds the lock releases it.
        if (payLockRef.current === attemptId) payLockRef.current = null;
      });
  }, [runAttempt]);

  const onSelectPaymentMethod = useCallback((paymentMethodId: string) => {
    if (payLockRef.current !== null) return;
    setLocal((current) => ({
      ...current,
      selectedPaymentMethodId: paymentMethodId,
      // A link left half paid gives the pay button back on a card pick.
      phase: current.phase === 'hostedOpened' ? 'idle' : current.phase,
    }));
  }, []);

  const onAddPaymentMethod = useCallback(() => {
    if (payLockRef.current !== null) return;
    latest.current.openAddPaymentMethod();
  }, []);

  const selectPastDueCard = useCallback((paymentMethodId: string) => {
    setLocal((current) => ({
      ...current,
      selectedPaymentMethodId: paymentMethodId,
      phase: current.phase === 'hostedOpened' ? 'idle' : current.phase,
    }));
  }, []);

  // The hosted page was opened: when the customer comes back to this window,
  // read the summary. Out of past due → toast; past due with no open invoice
  // → `settling`; otherwise keep waiting.
  useEffect(() => {
    if (local.phase !== 'hostedOpened') return undefined;
    const workspaceId = options.workspaceId;
    if (workspaceId === undefined) return undefined;
    let active = true;
    const onFocus = () => {
      if (hostedReadingRef.current) return;
      hostedReadingRef.current = true;
      void readSummary(workspaceId)
        .then(async (summary) => {
          if (!active || unmountedRef.current || summary === null) return;
          if (!isSubscriptionPastDue(summary.status)) {
            await finishPaid(hostedAmountRef.current);
            return;
          }
          if (summary.openInvoiceSource === 'none') {
            setLocal((current) =>
              current.phase === 'hostedOpened'
                ? { ...current, phase: 'settling' }
                : current,
            );
            void settle({
              workspaceId,
              amountForToast: hostedAmountRef.current,
            });
          }
        })
        .finally(() => {
          hostedReadingRef.current = false;
        });
    };
    window.addEventListener('focus', onFocus);
    return () => {
      active = false;
      window.removeEventListener('focus', onFocus);
    };
  }, [local.phase, options.workspaceId, finishPaid, readSummary, settle]);

  // The zone's own predicate; while the summary has not answered, the view is
  // built so the zone can draw its loading state.
  const showView = isPastDueZoneOn({
    zoneEnabled: options.enabled,
    isPastDue: options.summaryState !== 'loaded' || isPastDue,
    canPay: options.canPay,
  });

  const pastDuePayment = useMemo(
    () =>
      showView
        ? buildPastDuePaymentView({
            summary: options.subscription,
            summaryState: options.summaryState,
            daysPastDue: options.daysPastDue,
            paymentMethods: options.paymentMethods,
            paymentMethodDefaults: options.paymentMethodDefaults,
            local,
            callbacks: { onSelectPaymentMethod, onAddPaymentMethod, onPay },
          })
        : undefined,
    [
      showView,
      options.subscription,
      options.summaryState,
      options.daysPastDue,
      options.paymentMethods,
      options.paymentMethodDefaults,
      local,
      onSelectPaymentMethod,
      onAddPaymentMethod,
      onPay,
    ],
  );
  viewRef.current = pastDuePayment;

  // The zone opened already in `settling` (paid elsewhere, the webhook is
  // late): read until it leaves past due, once per workspace. No amount is
  // known here, so no toast.
  const derivedPhase = pastDuePayment?.phase;
  useEffect(() => {
    const workspaceId = options.workspaceId;
    if (
      !isPastDueZoneActive ||
      workspaceId === undefined ||
      derivedPhase !== 'settling' ||
      local.phase !== 'idle' ||
      settledOnLoadRef.current === workspaceId
    ) {
      return;
    }
    settledOnLoadRef.current = workspaceId;
    void settle({ workspaceId, amountForToast: null });
  }, [isPastDueZoneActive, options.workspaceId, derivedPhase, local.phase, settle]);

  return { pastDuePayment, isPastDueZoneActive, selectPastDueCard };
}
